/******************************************************************
 * ##: Build search suffix without token
 * Returns `?query` with `token` removed; other params unchanged (e.g. mode, theme). Never keeps credentials in URL.
 * @param {string} search - Query string (e.g. location.search)
 * @returns {string} - Suffix starting with `?` or empty string when no params remain
 * History:
 * 21-04-2026: Migrated comments from JSDoc to SalesPark block style
 ******************************************************************/
export const searchSuffixWithoutToken = (search) => {
  const params = new URLSearchParams(search);
  params.delete("token");
  const q = params.toString();
  return q ? `?${q}` : "";
};

/******************************************************************
 * ##: Path with preserved search (no token)
 * Builds pathname plus query from `search` with `token` removed; optional `extraParams` merged (set or delete keys).
 * @param {string} pathname - Target path (e.g. "/home")
 * @param {string} search - Raw query string (e.g. location.search)
 * @param {Record<string, string>} [extraParams] - Extra query pairs; null/undefined value removes the key
 * @returns {string} - Full path including optional `?` query string
 * History:
 * 21-04-2026: Migrated comments from JSDoc to SalesPark block style
 ******************************************************************/
export const pathWithPreservedSearch = (pathname, search, extraParams = {}) => {
  const params = new URLSearchParams(search);
  params.delete("token");
  Object.entries(extraParams).forEach(([key, value]) => {
    if (value === null || value === undefined) params.delete(key);
    else params.set(key, String(value));
  });
  const q = params.toString();
  return `${pathname}${q ? `?${q}` : ""}`;
};

/******************************************************************
 * Query parameter keys stripped on public auth routes (login, forgot-password, reset-password, OTP).
 * Removes token, mode, and theme from the query string while allowing e.g. returnUrl to pass through.
 ******************************************************************/
const AUTH_LAYOUT_PARAM_KEYS = ["token", "mode", "theme"];

/******************************************************************
 * ##: Path and query for auth layout routes
 * Strips token, mode, and theme; keeps other params (e.g. returnUrl). Same extra merge rules as pathWithPreservedSearch.
 * @param {string} pathname - Auth route path
 * @param {string} search - Raw query string (e.g. location.search)
 * @param {Record<string, string>} [extraParams] - Extra query pairs; null/undefined value removes the key
 * @returns {string} - Full path including optional `?` query string
 * History:
 * 21-04-2026: Migrated comments from JSDoc to SalesPark block style
 ******************************************************************/
export const pathForAuthRoute = (pathname, search, extraParams = {}) => {
  const params = new URLSearchParams(search);
  AUTH_LAYOUT_PARAM_KEYS.forEach((k) => params.delete(k));
  Object.entries(extraParams).forEach(([key, value]) => {
    if (value === null || value === undefined) params.delete(key);
    else params.set(key, String(value));
  });
  const q = params.toString();
  return `${pathname}${q ? `?${q}` : ""}`;
};

/******************************************************************
 * ##: Detect iframe embed mode in returnUrl
 * Decodes returnUrl and returns true when query contains mode=iframe. Only accepts same-origin-style paths starting with `/`.
 * @param {string} returnUrl - URL-encoded path + optional query
 * @returns {boolean} - True when decoded query has mode=iframe
 * History:
 * 21-04-2026: Migrated comments from JSDoc to SalesPark block style
 ******************************************************************/
export const returnUrlHasIframeMode = (returnUrl) => {
  if (!returnUrl || typeof returnUrl !== "string") return false;
  try {
    const decoded = decodeURIComponent(returnUrl.trim());
    if (!decoded.startsWith("/")) return false;
    const qIdx = decoded.indexOf("?");
    if (qIdx === -1) return false;
    const query = decoded.slice(qIdx + 1);
    return new URLSearchParams(query).get("mode") === "iframe";
  } catch {
    return false;
  }
};

/******************************************************************
 * ##: Map iframe returnUrl to full-app path
 * Same path and query as returnUrl but removes mode and theme for full-app login flows.
 * @param {string} returnUrl - URL-encoded path + optional query
 * @returns {string} - Path with query (without mode/theme), or "/" on invalid or empty input
 * History:
 * 21-04-2026: Migrated comments from JSDoc to SalesPark block style
 ******************************************************************/
export const iframeReturnUrlAsFullAppPath = (returnUrl) => {
  if (!returnUrl || typeof returnUrl !== "string") return "/";
  try {
    const decoded = decodeURIComponent(returnUrl.trim());
    if (!decoded.startsWith("/")) return "/";
    const qIdx = decoded.indexOf("?");
    const path = qIdx === -1 ? decoded : decoded.slice(0, qIdx);
    const query = qIdx === -1 ? "" : decoded.slice(qIdx + 1);
    const p = new URLSearchParams(query);
    p.delete("mode");
    p.delete("theme");
    const q = p.toString();
    return q ? `${path}?${q}` : path;
  } catch {
    return "/";
  }
};
